import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../api/client';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Check } from 'lucide-react';

export default function StartRunPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [fields, setFields] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/templates?status=published').then(r => setTemplates(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (selectedTemplate) {
      const defaults = {};
      (selectedTemplate.project_fields || []).forEach(f => {
        if (f.auto_fill === 'user.name' && user?.name) defaults[f.key] = user.name;
        else defaults[f.key] = '';
      });
      setFields(defaults);
    }
  }, [selectedTemplate, user]);

  const selectTemplate = async (t) => {
    try {
      const res = await api.get(`/templates/${t.id}`);
      setSelectedTemplate(res.data);
    } catch {
      setSelectedTemplate(t);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedTemplate) return;
    setLoading(true); setError('');
    try {
      const res = await api.post('/runs', { template_id: selectedTemplate.id, project_fields: fields });
      navigate(`/checklists/${res.data.run.id}/question`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to start run');
      toast.error(err.response?.data?.detail || 'Failed to start run');
    } finally { setLoading(false); }
  };

  return (
    <div className="p-6" data-testid="start-run-page">
      <p className="text-sm text-zinc-500 mb-6">Select a template and enter project details</p>
      {error && <div className="text-xs text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded-sm mb-4">{error}</div>}
      <div className="grid grid-cols-2 gap-8">
        <div>
          <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-400 mb-3 block">Select template</Label>
          <div className="space-y-2">
            {templates.length === 0 && <p className="text-sm text-zinc-400">No published templates available</p>}
            {templates.map(t => (
              <Card key={t.id} onClick={() => selectTemplate(t)} data-testid={`template-card-${t.id}`}
                className={`rounded-sm shadow-none cursor-pointer transition-all duration-150 ${
                  selectedTemplate?.id === t.id ? 'border-2 border-zinc-900' : 'border-zinc-200 hover:border-zinc-300'
                }`}>
                <CardContent className="p-3.5">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-zinc-800">{t.title}</span>
                    {selectedTemplate?.id === t.id && <Check size={14} className="text-zinc-900" />}
                  </div>
                  {t.description && <p className="text-xs text-zinc-400 mt-0.5">{t.description}</p>}
                  <p className="text-[10px] font-medium text-zinc-300 mt-1.5">{t.question_count || 0} questions &middot; v{t.version}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        <div>
          {selectedTemplate ? (
            <form onSubmit={handleSubmit}>
              <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-400 mb-3 block">Project details</Label>
              {(selectedTemplate.project_fields || []).sort((a, b) => a.order - b.order).map(f => (
                <div key={f.key} className="mb-3">
                  <Label className="text-xs font-semibold text-zinc-600 mb-1 block">
                    {f.label} {f.required && <span className="text-red-400">*</span>}
                  </Label>
                  <Input data-testid={`field-${f.key}`} value={fields[f.key] || ''} onChange={e => setFields({ ...fields, [f.key]: e.target.value })}
                    className="rounded-sm h-9" placeholder={f.placeholder || f.label} required={f.required} />
                </div>
              ))}
              <Button type="submit" disabled={loading} data-testid="start-run-submit" className="w-full rounded-sm h-10 text-sm font-semibold mt-2"
                style={{ background: 'var(--brand-primary, #1C3557)' }}>
                {loading ? 'Starting...' : 'Begin checklist'}
              </Button>
            </form>
          ) : (
            <div className="flex items-center justify-center h-48 text-sm text-zinc-400 bg-white border border-zinc-200 rounded-sm">
              Select a template to get started
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
